import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Building2,
  Hash,
  Shield,
  CalendarClock,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  MapPin,
  TrendingUp,
  ArrowRight,
  ShoppingBag
} from 'lucide-react';
import UserLayout from './user/UserLayout';
import OrderStatsWidget from './OrderStatsWidget';
import { orderService } from '../services/orderService';
import { SkeletonRows } from './shared/Skeleton';
import { showError } from '../utils/notificationHandler';
import { format } from 'date-fns';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

const Account = () => {
  const history = useHistory();
  const [currentUser, setCurrentUser] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [ordersSummary, setOrdersSummary] = useState(null);
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAccountData();
  }, []);

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Profile / account details
      const { data: account, error: accountError } = await supabase
        .from('accounts')
        .select('*')
        .eq('id', user.id)
        .single();
      if (accountError) throw accountError;
      setCurrentUser(account);

      // Recent orders + summary
      const [{ orders }, summary] = await Promise.all([
        orderService.getAllOrders({
          limit: 5,
          orderBy: 'created_at',
          orderDirection: 'desc'
        }),
        orderService.getOutstandingOrdersSummary()
      ]);
      setRecentOrders(orders || []);
      setOrdersSummary(summary);

      // Delivery addresses (company-scoped by deb_id)
      if (account?.deb_id) {
        const { data: addresses, error: addressError } = await supabase
          .from('delivery_addresses')
          .select('*')
          .eq('company_deb_id', account.deb_id);
        if (addressError) throw addressError;
        setDeliveryAddresses(addresses || []);
      }
    } catch (error) {
      console.error('Error fetching account data:', error);
      showError(error, 'Failed to load account information');
    } finally {
      setLoading(false);
    }
  };

  const DetailItem = ({ icon: Icon, label, value }) => (
    <div className="flex items-start space-x-3">
      <Icon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-sm text-gray-600 mb-0.5">{label}</p>
        <p className="font-semibold text-gray-900 break-words">{value || 'N/A'}</p>
      </div>
    </div>
  );

  const roleLabel = (user) => {
    if (user?.is_admin) return 'System Admin';
    if (user?.is_staff) return 'Staff';
    if (user?.is_company_admin) return 'Company Admin';
    return user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User';
  };

  return (
    <UserLayout>
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">Account Overview</h1>
          <p className="text-gray-600 mt-2">
            View your account details, recent orders, and delivery locations
          </p>
        </motion.div>

        {loading ? (
          <SkeletonRows rows={5} />
        ) : (
          <>
            {/* Account Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"
            >
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                  {currentUser?.avatar_url ? (
                    <img
                      src={currentUser.avatar_url}
                      alt="Profile"
                      className="h-14 w-14 rounded-full object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-brand flex items-center justify-center">
                      <User className="h-7 w-7 text-brand-dark" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {currentUser?.full_name || 'Account Details'}
                    </h2>
                    <p className="text-sm text-gray-600">{currentUser?.company_name}</p>
                  </div>
                </div>
                {currentUser?.is_active ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="h-4 w-4 mr-1" /> Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="h-4 w-4 mr-1" /> Inactive
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <DetailItem icon={User} label="Full Name" value={currentUser?.full_name} />
                <DetailItem icon={Mail} label="Email" value={currentUser?.email} />
                <DetailItem icon={Building2} label="Company" value={currentUser?.company_name} />
                <DetailItem icon={Hash} label="MHS Account Number" value={currentUser?.mhs_account_number} />
                <DetailItem icon={Shield} label="Role" value={roleLabel(currentUser)} />
                <DetailItem icon={CalendarClock} label="Member Since" value={formatDate(currentUser?.created_at)} />
                <DetailItem icon={Clock} label="Last Login" value={formatDate(currentUser?.last_login)} />
              </div>
            </motion.div>

            {/* Account Spend — company admins only */}
            {currentUser?.is_company_admin && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-6 pb-4 border-b border-gray-200">
                  Account Spend
                </h2>
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="h-14 w-14 rounded-full bg-brand/20 flex items-center justify-center mb-4">
                    <TrendingUp className="h-7 w-7 text-brand-dark" />
                  </div>
                  <p className="text-lg font-semibold text-gray-900">Coming soon</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Company spend analytics will be available here shortly.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Order Statistics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mb-6"
            >
              <OrderStatsWidget />
            </motion.div>

            {/* Recent Orders */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"
            >
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Recent Orders</h2>
                <button
                  onClick={() => history.push('/recent-orders')}
                  className="inline-flex items-center text-sm font-medium text-brand-dark hover:underline"
                >
                  View all orders <ArrowRight className="h-4 w-4 ml-1" />
                </button>
              </div>

              {recentOrders.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500">No orders found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentOrders.map((order) => {
                    const orderTotal =
                      order.order_total_inc_tax ||
                      orderService.calculatePortalOrderTotal(order.cart_items);
                    return (
                      <div
                        key={order.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3 min-w-0">
                          <ShoppingBag className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate">
                              {order.order_number}
                            </p>
                            <p className="text-sm text-gray-500">
                              {format(new Date(order.created_at), 'PPp')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 flex-shrink-0">
                          <span className="font-semibold text-gray-900 hidden sm:block">
                            {orderService.formatCurrency(orderTotal)}
                          </span>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium border ${orderService.getStatusColor(order.status)}`}
                          >
                            {orderService.getStatusDisplayName(order.status) || 'Pending'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {ordersSummary && (
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
                  <span>{ordersSummary.total_orders} total order{ordersSummary.total_orders !== 1 ? 's' : ''}</span>
                  {ordersSummary.total_outstanding > 0 && (
                    <span className="text-red-700 font-medium">
                      {ordersSummary.total_outstanding} outstanding
                    </span>
                  )}
                </div>
              )}
            </motion.div>

            {/* Delivery Addresses */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-6 pb-4 border-b border-gray-200">
                Delivery Addresses
              </h2>

              {deliveryAddresses.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500">No delivery addresses on file</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {deliveryAddresses.map((address) => (
                    <div
                      key={address.id}
                      className="flex items-start space-x-3 p-4 rounded-lg border border-gray-200"
                    >
                      <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="font-semibold text-gray-900">
                            {address.address_line1}
                          </p>
                          {address.is_primary && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-brand/20 text-brand-dark">
                              Primary
                            </span>
                          )}
                        </div>
                        {address.address_line2 && (
                          <p className="text-sm text-gray-600">{address.address_line2}</p>
                        )}
                        <p className="text-sm text-gray-600">
                          {[address.city, address.state, address.postal_code]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                        {address.country && (
                          <p className="text-sm text-gray-500">{address.country}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </>
        )}
      </div>
    </UserLayout>
  );
};

export default Account;
